import { useEffect, useMemo, useRef, useState } from 'react';

const RULER_SIZE = 20;

export const DROP_GUIDE_EVENT = 'craftara:drop-guide';

interface RulersProps {
  stageRef: React.RefObject<HTMLDivElement | null>;
  zoom: number;
  artboardWidth: number;
  artboardHeight: number;
  visible: boolean;
}

function pickStep(canvasPxPerTick: number): number {
  const candidates = [1, 2, 5, 10, 25, 50, 100, 200, 500, 1000];
  for (const step of candidates) {
    if (step * canvasPxPerTick >= 40) return step;
  }
  return 1000;
}

export default function Rulers({
  stageRef,
  zoom,
  artboardWidth,
  artboardHeight,
  visible,
}: RulersProps) {
  const hRef = useRef<HTMLCanvasElement>(null);
  const vRef = useRef<HTMLCanvasElement>(null);
  const [scroll, setScroll] = useState({ left: 0, top: 0 });
  const [stageSize, setStageSize] = useState({ w: 0, h: 0 });
  const dragRef = useRef<{ orientation: 'v' | 'h' } | null>(null);
  const [preview, setPreview] = useState<{
    orientation: 'v' | 'h';
    clientX: number;
    clientY: number;
  } | null>(null);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || !visible) return;

    const sync = () => {
      setScroll({ left: stage.scrollLeft, top: stage.scrollTop });
      setStageSize({ w: stage.clientWidth, h: stage.clientHeight });
    };

    sync();
    stage.addEventListener('scroll', sync, { passive: true });
    const ro = new ResizeObserver(sync);
    ro.observe(stage);
    window.addEventListener('resize', sync);
    return () => {
      stage.removeEventListener('scroll', sync);
      ro.disconnect();
      window.removeEventListener('resize', sync);
    };
  }, [stageRef, visible, zoom]);

  const scale = zoom / 100;

  const ticks = useMemo(() => {
    const step = pickStep(scale);
    return { step, scale };
  }, [scale]);

  useEffect(() => {
    if (!visible) return;
    const hCanvas = hRef.current;
    const vCanvas = vRef.current;
    if (!hCanvas || !vCanvas) return;

    const dpr = window.devicePixelRatio || 1;
    const hW = Math.max(1, stageSize.w);
    const vH = Math.max(1, stageSize.h);

    hCanvas.width = hW * dpr;
    hCanvas.height = RULER_SIZE * dpr;
    hCanvas.style.width = `${hW}px`;
    hCanvas.style.height = `${RULER_SIZE}px`;

    vCanvas.width = RULER_SIZE * dpr;
    vCanvas.height = vH * dpr;
    vCanvas.style.width = `${RULER_SIZE}px`;
    vCanvas.style.height = `${vH}px`;

    const hCtx = hCanvas.getContext('2d');
    const vCtx = vCanvas.getContext('2d');
    if (!hCtx || !vCtx) return;

    hCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
    vCtx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const bg = '#2a2a2a';
    const fg = '#8a8a8a';
    const accent = '#c8c8c8';

    hCtx.fillStyle = bg;
    hCtx.fillRect(0, 0, hW, RULER_SIZE);
    hCtx.strokeStyle = fg;
    hCtx.fillStyle = accent;
    hCtx.font = '9px ui-sans-serif, system-ui, sans-serif';
    hCtx.textBaseline = 'top';

    const contentW = artboardWidth * ticks.scale;
    const contentH = artboardHeight * ticks.scale;
    const originX = Math.max(0, (stageSize.w - contentW) / 2) - scroll.left;
    const originY = Math.max(0, (stageSize.h - contentH) / 2) - scroll.top;

    const { step } = ticks;
    const startX = Math.floor((-originX / ticks.scale) / step) * step - step;
    const endX = Math.ceil((hW - originX) / ticks.scale / step) * step + step;

    for (let x = startX; x <= endX; x += step) {
      const px = originX + x * ticks.scale;
      if (px < -20 || px > hW + 20) continue;
      const major = x % (step * 2) === 0;
      hCtx.beginPath();
      hCtx.moveTo(px + 0.5, major ? 6 : 12);
      hCtx.lineTo(px + 0.5, RULER_SIZE);
      hCtx.stroke();
      if (major) {
        hCtx.fillText(String(x), px + 3, 2);
      }
    }

    vCtx.fillStyle = bg;
    vCtx.fillRect(0, 0, RULER_SIZE, vH);
    vCtx.strokeStyle = fg;
    vCtx.fillStyle = accent;
    vCtx.font = '9px ui-sans-serif, system-ui, sans-serif';

    const startY = Math.floor((-originY / ticks.scale) / step) * step - step;
    const endY = Math.ceil((vH - originY) / ticks.scale / step) * step + step;

    for (let y = startY; y <= endY; y += step) {
      const py = originY + y * ticks.scale;
      if (py < -20 || py > vH + 20) continue;
      const major = y % (step * 2) === 0;
      vCtx.beginPath();
      vCtx.moveTo(major ? 6 : 12, py + 0.5);
      vCtx.lineTo(RULER_SIZE, py + 0.5);
      vCtx.stroke();
      if (major) {
        vCtx.save();
        vCtx.translate(2, py + 3);
        vCtx.rotate(-Math.PI / 2);
        vCtx.fillText(String(y), 0, 0);
        vCtx.restore();
      }
    }
  }, [visible, stageSize, scroll, ticks, artboardWidth, artboardHeight]);

  useEffect(() => {
    if (!visible) return;

    const onMove = (e: MouseEvent) => {
      if (!dragRef.current) return;
      setPreview({
        orientation: dragRef.current.orientation,
        clientX: e.clientX,
        clientY: e.clientY,
      });
    };

    const onUp = (e: MouseEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      dragRef.current = null;
      setPreview(null);
      window.dispatchEvent(
        new CustomEvent(DROP_GUIDE_EVENT, {
          detail: {
            orientation: drag.orientation,
            clientX: e.clientX,
            clientY: e.clientY,
          },
        })
      );
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [visible]);

  const startDrag = (orientation: 'v' | 'h') => (e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = { orientation };
    setPreview({ orientation, clientX: e.clientX, clientY: e.clientY });
  };

  if (!visible) return null;

  return (
    <>
      <div
        className="ps-ruler-corner"
        aria-hidden
        title="Drag from rulers onto the artboard to create guides"
      />
      <div
        className="ps-ruler-h"
        aria-hidden
        title="Drag down to create a horizontal guide"
        onMouseDown={startDrag('h')}
        style={{ cursor: 'ns-resize' }}
      >
        <canvas ref={hRef} />
      </div>
      <div
        className="ps-ruler-v"
        aria-hidden
        title="Drag right to create a vertical guide"
        onMouseDown={startDrag('v')}
        style={{ cursor: 'ew-resize' }}
      >
        <canvas ref={vRef} />
      </div>
      {preview && (
        <div
          className={`ps-guide-preview ${preview.orientation}`}
          style={
            preview.orientation === 'v'
              ? { left: preview.clientX }
              : { top: preview.clientY }
          }
        />
      )}
    </>
  );
}

export const RULER_THICKNESS = RULER_SIZE;
